import { css, html, LitElement, nothing } from 'lit';

import '../library/fields/d-view-html.js';
import '../library/elements/d-action.js';
import { LocalDate } from 'src/utilities/local-date.js';
import { customElement, property } from 'lit/decorators.js';
import '../library/elements/d-smooth-resize.js';
import { LocalDateTime } from 'src/utilities/local-date-time.js';
import { calculateUpdateStatus } from 'src/models/resolvers/calculate-update-status.js';
import { t } from 'src/i18n';
import type { UpdateSectionItem } from './update-section-item';

/**
 *
 */
@customElement('d-update-section')
export class DUpdateSection extends LitElement {
  static readonly styles = css`
    :host {
      display: block;
    }

    #revisionFieldWrapper {
      margin: 20px 0;
    }

    :host([help-section-present]) #revisionFieldWrapper {
      margin-top: 0;
    }

    #revisionField {
      min-height: 38px;
      position: relative;
      max-width: 724px;
      box-sizing: border-box;
      padding: 16px 20px 14px 20px;
      border-radius: 20px;
      word-wrap: normal;
    }

    #revisionField.templateDeleted {
      background-color: hsl(20, 100%, 96%);
      border: 1px solid hsl(20, 100%, 90%);
    }

    #revisionField.templateUpdated {
      background-color: hsl(190, 60%, 94%);
      border: 1px solid hsl(190, 60%, 88%);
    }

    #revisionField.documentUpdated {
      background-color: hsl(100, 80%, 92%);
      border: 1px solid hsl(100, 80%, 80%);
      padding: 8px 8px 0 13px;
    }

    .revisionHeader:before {
      display: inline-block;
      content: '';
      margin-left: -2px;
      margin-right: 4px;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      position: relative;
      top: 2px;
    }

    .templateDeleted .revisionHeader:before {
      background-color: var(--alertColor);
    }

    .templateUpdated .revisionHeader:before {
      background-color: var(--themeColorDarkerOne);
    }

    .documentUpdated .revisionHeader:before {
      background-color: hsl(100, 80%, 40%);
    }

    .revisionHeader .documentUpdated {
      white-space: normal;
    }

    .revisionHeader .title {
      font-weight: 600;
    }

    d-view-info {
      margin-top: 12px;
    }

    .actions {
      margin: 6px -8px -6px -8px;
    }
  `;
  @property({ type: Boolean })
  opened = false;
  @property({ type: Boolean, attribute: 'help-section-present', reflect: true })
  helpSectionPresent = false;
  @property({ type: Boolean })
  noAnimation = false;
  @property({ type: String })
  entityType = '';
  @property({ type: String })
  entityId = '';
  @property({ type: Object })
  item!: UpdateSectionItem;
  @property({ type: Boolean })
  singleUserVersion = false;
  @property({ type: String })
  type = '';
  @property({ type: String })
  title = '';
  @property({ type: Object })
  today!: LocalDate;
  @property({ type: String })
  dateTime = '';
  @property({ type: String })
  byline = '';
  @property({ type: String })
  revisionContent = '';

  private get updateSectionItem() {
    const status = calculateUpdateStatus(this.item, this.today);

    if (status === 'templateDeleted') {
      return {
        type: 'templateDeleted',
        title: t('content.updateSectionSuggestedDeletion'),
        dateTime: this._formatDateTime(this.item.templateDeleted),
        byline: t('content.updateSectionByTrinnvisTeam'),
        revisionContent: this.item.templateDeletedMessage,
        opened: true,
      };
    } else if (status === 'templateUpdated') {
      return {
        type: 'templateUpdated',
        title: t('content.updateSectionSuggestedUpdate'),
        dateTime: this._formatDateTime(this.item.templateUpdated),
        byline: t('content.updateSectionByTrinnvisTeam'),
        revisionContent: '',
        opened: true,
      };
    } else if (
      this.item.contentLastModified !== '' &&
      this.today.minusDays(30).isBefore(LocalDate.fromString(this.item.contentLastModified))
    ) {
      let updateTerm = t('content.updateSectionUpdated');
      if (this.item.contentLastModifiedWasCreate) {
        updateTerm = t('content.updateSectionCreated');
      }
      let byline = t('content.updateSectionByline', { name: this.item.contentLastModifiedBy });
      if (this.singleUserVersion && this.item.contentLastModifiedBy?.toLowerCase() !== 'trinnvis-teamet') {
        byline = t('content.updateSectionByMe');
      }
      return {
        type: 'documentUpdated',
        title: updateTerm,
        dateTime: this._formatRelativeDays(LocalDate.fromString(this.item.contentLastModified)),
        byline: byline,
        revisionContent: '',
        opened: true,
      };
    } else {
      return {
        type: '',
        title: '',
        dateTime: '',
        byline: '',
        revisionContent: '',
        opened: false,
      };
    }
  }

  _formatRelativeDays(date: LocalDate) {
    const days = this.today.toEpochDay() - date.toEpochDay();
    let timeText = t('content.updateSectionToday');
    if (days === 1) {
      timeText = t('content.updateSectionYesterday');
    }
    if (days === 2) {
      timeText = t('content.updateSectionDayBeforeYesterday');
    }
    if (days === 3) {
      timeText = t('content.updateSectionThreeDaysAgo');
    }
    if (days === 4) {
      timeText = t('content.updateSectionFourDaysAgo');
    }
    if (days === 5) {
      timeText = t('content.updateSectionFiveDaysAgo');
    }
    if (days === 6) {
      timeText = t('content.updateSectionSixDaysAgo');
    }
    if (days === 7) {
      timeText = t('content.updateSectionOneWeekAgo');
    }
    if (days > 7) {
      timeText = t('content.updateSectionDaysAgo', { days });
    }
    return timeText;
  }

  _hostClasses(opened) {
    if (opened) {
      return 'opened';
    }
    return '';
  }

  _formatDateTime(dateTime) {
    return LocalDateTime.fromString(dateTime.replace('T', ' ')).toStringForDisplay();
  }

  _view() {
    this.dispatchEvent(new CustomEvent('view-suggestion', { bubbles: true, composed: true }));
  }

  _delete() {
    this.dispatchEvent(new CustomEvent('delete', { bubbles: true, composed: true }));
  }

  _decline() {
    this.dispatchEvent(
      new CustomEvent('decline', {
        bubbles: true,
        composed: true,
        detail: {
          entityType: this.entityType,
          entityId: this.entityId,
        },
      }),
    );
    this.opened = false;
  }

  infoContent(item) {
    if (item.type === 'templateDeleted') {
      return t('content.updateSectionDeletedInfo', { content: item.revisionContent });
    }
    if (item.type === 'templateUpdated') {
      return t('content.updateSectionUpdatedInfo');
    }
    return '';
  }

  render() {
    const updateSectionItem = this.updateSectionItem;
    if (updateSectionItem.type === '') {
      return nothing;
    }
    return html`
      <d-smooth-resize>
        <div id="revisionFieldWrapper">
          <div class="${updateSectionItem.type}" id="revisionField">
            <div class="revisionHeader">
              <span class="title">${updateSectionItem.title}</span>
              <span class="noWrap">${updateSectionItem.dateTime} </span>
              <span class="noWrap">${updateSectionItem.byline}</span>
            </div>
            ${this.infoContent(updateSectionItem) !== ''
              ? html` <d-view-info .content=${this.infoContent(updateSectionItem)}></d-view-info>`
              : nothing}
            ${updateSectionItem.type === 'templateDeleted'
              ? html`
                  <div class="actions">
                    <d-action delete @click=${() => this._delete()}>${t('content.updateSectionDelete')}</d-action>
                    <d-action @click=${() => this._decline()}>${t('content.updateSectionDismissAlert')}</d-action>
                  </div>
                `
              : nothing}
            ${updateSectionItem.type === 'templateUpdated'
              ? html`
                  <div class="actions">
                    <d-action @click=${() => this._view()}>${t('content.updateSectionViewSuggestion')}</d-action>
                    <d-action @click=${() => this._decline()}>${t('content.updateSectionDismissAlert')}</d-action>
                  </div>
                `
              : nothing}
          </div>
        </div>
      </d-smooth-resize>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-update-section': DUpdateSection;
  }
}
